import { Ripple } from '@components/ui/Ripple/Ripple';
import { Spinner } from '@components/ui/Spinner/Spinner';
import {
  Button as HeadlessButton,
  ButtonProps as HeadlessButtonProps
} from '@headlessui/react';
import { type ElementType, forwardRef, PropsWithChildren } from 'react';
import { VariantProps } from 'tailwind-variants';
import { buttonIconStyles } from './ButtonIcon.style';

type ButtonVariants = VariantProps<typeof buttonIconStyles>;
type ButtonVariant = ButtonVariants['variant'];
type ButtonSize = ButtonVariants['size'];
type ButtonColor = ButtonVariants['color'];

type ButtonRenderProps = {
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  rounded?: boolean;
  flat?: boolean;
  tile?: boolean;
};

export type ButtonIconProps<E extends ElementType = 'button'> =
  PropsWithChildren<
    {
      className?: string;
      loading?: boolean;
    } & Omit<HeadlessButtonProps<E>, 'color'> &
      ButtonRenderProps
  >;

const loaderStrokeMapper: Record<NonNullable<ButtonSize>, number> = {
  xs: 2,
  sm: 3,
  md: 3,
  lg: 4,
  xl: 5
};

export const ButtonIcon = forwardRef<any, ButtonIconProps>(
  (
    {
      as,
      variant,
      color,
      size,
      rounded,
      flat,
      tile,
      className,
      loading,
      children,
      ...props
    },
    ref
  ) => {
    const buttonIconClassNames = buttonIconStyles({
      color: color,
      variant: variant,
      size: size,
      rounded: rounded,
      flat: flat,
      tile: tile
    });

    return (
      <HeadlessButton
        as={as}
        {...(props as any)}
        ref={ref}
        className={buttonIconClassNames.base({ className })}
        data-ui-disabled={props.disabled || undefined}
        data-loading={loading || undefined}
        data-component="button-icon"
        disabled={props.disabled || loading}
      >
        {!loading ? (
          children
        ) : (
          <Spinner
            strokeWidth={size && loaderStrokeMapper[size]}
            className={buttonIconClassNames.loader()}
            theme="light"
          />
        )}

        {/* Hover Overlay */}
        {!props.disabled ? (
          <div className={buttonIconClassNames.overlay()} />
        ) : null}

        {/* Click Ripple Effect */}
        {!props.disabled && !loading ? (
          <Ripple spanClassName={buttonIconClassNames.ripple()} />
        ) : null}
      </HeadlessButton>
    );
  }
);
